import {
  ContentItemStatus,
  ContentItemType,
  ContentTones,
  ContentWordCount
} from '@/modules/content-item/models/content-item-resource.model'
import { CampaignStatus } from '@/modules/base-campaign/models/base-campaign-resource.model'
import { BasicUserResourceModel } from '@/modules/user/models/user-resource.model'
import { Data } from '@/modules/shared/models/data'
import { ProfitType } from '@/modules/profit/models/profit-resource.model'
import {
  ContentItemCollectionInAdminModel,
  ContentItemStepOneCollection
} from '@/modules/content-item/models/content-item-collection.model'
import { MediaResourceModel } from '@/modules/media/models/media-resource.model'
import { InvoiceForModelCollectionModel } from '@/modules/invoice/models/invoice-for-model-collection.model'
import { BasicCouponResourceModel } from '@/modules/coupon/models/coupon-resource.model'
import { BillingInfoResourceModel } from '@/modules/billing-info/models/billing-info-resource'
import { OwnBankInfoResourceModel } from '@/modules/bank-account/models/bank-account-resource.model'

export enum ContentCampaignItemStatus {
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  EDITING = 'editing',
  PENDING = 'pending',
  QUEUE = 'queue',
  REJECTED = 'rejected',
  REVIEW = 'review',
  WAITING_PAYMENT = 'waiting-payment'
}

export enum ContentCampaignTypes {
  FOR_FREE = 'free'
}

export interface BasicContentCampaignResourceModel {
  id: number
  title: string | null
}

export interface ContentCampaignResourceModel {
  id: number,
  title: string | null,
  status: CampaignStatus,
  campaign_type: string,
  description: string | null,
  references_url: string,
  attachments: MediaResourceModel[] | null,
  running_at: string | null,
  invoices: InvoiceForModelCollectionModel[] | null,
  total_items_count: number,
  items_count: ContentCampaignItemsCount
  items: ContentItemCollectionInAdminModel[] | null,
}

export interface ContentCampaignResourceModelInAdmin extends ContentCampaignResourceModel {
  price: number | null,
  system_discount: number,
  manual_discount: number,
  discount: number | null,
  payable: number | null,
  profit?: number,
  admin_note: string | null,
  campaign_code: string | null
  created_at: string,
  updated_at: string,
  completed_at: string | null,
  coupon: BasicCouponResourceModel,
  user: BasicUserResourceModel,
  step: number | null
}

export interface ContentCampaignUpdatableAttributesResourceModel {
  title: string | null
  description: string | null
  admin_note: string | null
  status: CampaignStatus
  running_at: string | null
  references_url: string
  completed_at: string | null
  user: BasicUserResourceModel
}

export interface ContentCampaignStepThreeResource {
  id: number
  cart: ContentCampaignCartResource
  billing_info: BillingInfoResourceModel
  own_bank_info: OwnBankInfoResourceModel
}

export interface ContentCampaignCartResource {
  id: number
  items_count: number
  price: number
  coupon: BasicCouponResourceModel
  coupon_discount: number
  discount: number
  net_payable: number
  tax: number
  payable: number
  profit?: number
}

export interface ContentCampaignUpdatableAttributesResourceWithModel {
  statuses: { [key in CampaignStatus]: string }
}

export interface ContentCampaignResourceWithModel {
  statuses: { [key in CampaignStatus]: string }
  item_statuses: { [key in ContentItemStatus]: string }
  content_types: { [key in ContentItemType]: string },
}

export interface ContentCampaignResourceWithModelInAdmin extends ContentCampaignResourceWithModel {
  item_content_types: { [key in ContentItemType]: string }
  item_tones: { [key in ContentTones]: string }
  item_word_counts: { [key in ContentWordCount]: string }
  profit_log_types: { [key in ProfitType]: string }
}

export interface ContentCampaignStepOneGetResourceModel {
  id?: number
  title: string,
  description: string,
  references_url: string,
  attachments: Array<MediaResourceModel | File>,
  items: ContentItemStepOneCollection[]
}

export interface ContentCampaignStepOneGetResourceWith {
  word_counts: { [key in ContentWordCount]: string }
  content_types: { [key in ContentItemType]: string }
  content_tones: { [key in ContentTones]: string }
  word_rates:  { [key in ContentItemType]: number }
  suggested_campaign_title?: string
}

export type ContentCampaignStepOneGetResourceType =
  Data<ContentCampaignStepOneGetResourceModel>
  & ContentCampaignStepOneGetResourceWith

export type ContentCampaignItemsCount = {
  [key in ContentItemStatus]: number
}

export type ContentCampaignResourceType =
  Data<ContentCampaignResourceModel>
  & ContentCampaignResourceWithModel

export type ContentCampaignResourceTypeInAdmin =
  Data<ContentCampaignResourceModelInAdmin>
  & ContentCampaignResourceWithModelInAdmin

export type ContentCampaignUpdatableAttributesResourceTypeInAdmin =
  Data<ContentCampaignUpdatableAttributesResourceModel>
  & ContentCampaignUpdatableAttributesResourceWithModel
